import { ApiError, type BillingCalendarSettings, queryKeys } from '@mevaser/shared'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Switch,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

const CLOCK_TIME = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * Keeps recurring phone-plan charges off Shabbos and Yom Tov (t1129). The
 * times are on the support time zone's clock (Settings → Support).
 */
export function BillingCalendarCard() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.billingCalendarSettings(),
    queryFn: () => api.get<BillingCalendarSettings>('/settings/billing-calendar'),
  })

  const [enabled, setEnabled] = useState(false)
  const [nightChargeTime, setNightChargeTime] = useState('22:00')
  const [dayChargeTime, setDayChargeTime] = useState('12:00')
  const [erevCutoff, setErevCutoff] = useState('15:00')
  const [combineSameDay, setCombineSameDay] = useState(true)
  const [noticeEmail, setNoticeEmail] = useState(false)
  const [noticeSms, setNoticeSms] = useState(false)
  const [noticeDays, setNoticeDays] = useState('2')

  useEffect(() => {
    if (!data) return
    setEnabled(data.enabled)
    setNightChargeTime(data.nightChargeTime)
    setDayChargeTime(data.dayChargeTime)
    setErevCutoff(data.erevCutoff)
    setCombineSameDay(data.combineSameDay)
    setNoticeEmail(data.chargeNoticeEmailEnabled)
    setNoticeSms(data.chargeNoticeSmsEnabled)
    setNoticeDays(String(data.chargeNoticeLeadDays))
  }, [data])

  const save = useMutation({
    mutationFn: () =>
      api.patch('/settings/billing-calendar', {
        enabled,
        nightChargeTime,
        dayChargeTime,
        erevCutoff,
        combineSameDay,
        chargeNoticeEmailEnabled: noticeEmail,
        chargeNoticeSmsEnabled: noticeSms,
        chargeNoticeLeadDays: Number(noticeDays),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.billingCalendarSettings() })
      toast.success(
        'Saved',
        enabled
          ? 'Renewals in the next few days were checked and moved where needed.'
          : 'Plan charges follow their normal schedule.',
      )
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const invalid =
    !CLOCK_TIME.test(nightChargeTime) ||
    !CLOCK_TIME.test(dayChargeTime) ||
    !CLOCK_TIME.test(erevCutoff) ||
    !/^\d+$/.test(noticeDays) ||
    Number(noticeDays) < 1 ||
    Number(noticeDays) > 14
  const unchanged = data
    ? data.enabled === enabled &&
      data.nightChargeTime === nightChargeTime &&
      data.dayChargeTime === dayChargeTime &&
      data.erevCutoff === erevCutoff &&
      data.combineSameDay === combineSameDay &&
      data.chargeNoticeEmailEnabled === noticeEmail &&
      data.chargeNoticeSmsEnabled === noticeSms &&
      String(data.chargeNoticeLeadDays) === noticeDays
    : true

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Plan charges</CardTitle>
        <CardDescription>
          Combines same-day phone-plan charges, and keeps them and their retries off Shabbos and Yom
          Tov.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Charge a customer’s same-day plans together</span>
          <Switch
            checked={combineSameDay}
            disabled={isLoading}
            onCheckedChange={setCombineSameDay}
          />
        </div>
        <p className="text-xs text-foreground-muted">
          When a customer has two or more card-billed plans renewing on the same day to the same
          saved card, they are taken as one payment an hour after the last renewal. If that payment
          is declined, each plan is charged on its own.
        </p>

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Email customers an invoice before a charge</span>
          <Switch checked={noticeEmail} disabled={isLoading} onCheckedChange={setNoticeEmail} />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Text customers a reminder before a charge</span>
          <Switch checked={noticeSms} disabled={isLoading} onCheckedChange={setNoticeSms} />
        </div>
        <p className="text-xs text-foreground-muted">
          Each morning, customers whose card-billed plans are about to renew are sent one itemised
          invoice per charge day (all their plans renewing that day, with the total). Each charge is
          only announced once, and a charge whose date moves is announced again. Nothing is sent on
          Shabbos or Yom Tov — the notice goes out the day before instead. Texts go only to numbers
          that can receive them and need the texting provider to be set up.
        </p>
        <Field
          label="Days before the charge"
          hint="Between 1 and 14. Two is the usual choice."
          error={
            /^\d+$/.test(noticeDays) && Number(noticeDays) >= 1 && Number(noticeDays) <= 14
              ? undefined
              : 'Enter a number from 1 to 14'
          }
        >
          {(props) => (
            <Input
              {...props}
              type="number"
              min={1}
              max={14}
              disabled={isLoading}
              value={noticeDays}
              onChange={(e) => setNoticeDays(e.target.value)}
            />
          )}
        </Field>

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Don’t charge plans on Shabbos or Yom Tov</span>
          <Switch checked={enabled} disabled={isLoading} onCheckedChange={setEnabled} />
        </div>
        <p className="text-xs text-foreground-muted">
          A charge due on Shabbos, or on the second day of Yom Tov, is taken at night once it is
          over. A charge due on the first day of a Yom Tov is taken the day before, which also moves
          that plan’s later renewal dates a day earlier and credits the days already paid. A Shabbos
          that leads into a Yom Tov counts as part of it. A declined charge is tried again after 3,
          5 and 7 days, on the same rules.
        </p>

        <Field
          label="Night charge time"
          hint="When a charge held back for Shabbos or Yom Tov is taken, on the night it ends. Pick a time after nightfall all year, such as 22:00."
          error={CLOCK_TIME.test(nightChargeTime) ? undefined : 'Use 24-hour time, like 22:00'}
        >
          {(props) => (
            <Input
              {...props}
              type="time"
              disabled={isLoading}
              value={nightChargeTime}
              invalid={!CLOCK_TIME.test(nightChargeTime)}
              onChange={(e) => setNightChargeTime(e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Day-before charge time"
          hint="When a charge moved ahead of a Yom Tov is taken, on the day before. Keep it comfortably before candle lighting."
          error={CLOCK_TIME.test(dayChargeTime) ? undefined : 'Use 24-hour time, like 12:00'}
        >
          {(props) => (
            <Input
              {...props}
              type="time"
              disabled={isLoading}
              value={dayChargeTime}
              invalid={!CLOCK_TIME.test(dayChargeTime)}
              onChange={(e) => setDayChargeTime(e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Erev cutoff"
          hint="A charge due on the afternoon or evening before Shabbos or Yom Tov, from this time on, is treated as falling on it."
          error={CLOCK_TIME.test(erevCutoff) ? undefined : 'Use 24-hour time, like 15:00'}
        >
          {(props) => (
            <Input
              {...props}
              type="time"
              disabled={isLoading}
              value={erevCutoff}
              invalid={!CLOCK_TIME.test(erevCutoff)}
              onChange={(e) => setErevCutoff(e.target.value)}
            />
          )}
        </Field>

        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={invalid || unchanged || save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}
